'use client';

import { useState } from 'react';
import { Globe, Download, Copy, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { sitemapGeneratorContent } from '../../data/sitemap-generator-content';

export default function SitemapGenerator() {
    const [url, setUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sitemap, setSitemap] = useState('');
    const [isCopied, setIsCopied] = useState(false);

    const generateSitemap = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!url) {
            toast.error('Please enter a valid URL');
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch('/api/sitemap', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ url }),
            });

            if (!response.ok) {
                throw new Error('Failed to generate sitemap');
            }

            const data = await response.text();
            setSitemap(data);
            toast.success('Sitemap generated successfully!');
        } catch (error) {
            console.error('Error generating sitemap:', error);
            toast.error('Failed to generate sitemap. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(sitemap);
        setIsCopied(true);
        toast.success('Copied to clipboard!');
        setTimeout(() => setIsCopied(false), 2000);
    };

    const downloadSitemap = () => {
        const blob = new Blob([sitemap], { type: 'application/xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'sitemap.xml';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="text-center mb-8">
                <ToolPageIntro title="Sitemap Generator - Free Online XML Sitemap Creator">
                Generate XML sitemaps for websites with our free online tool. Improve SEO and help search engines crawl your site.
            </ToolPageIntro>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
                <form onSubmit={generateSitemap} className="space-y-4">
                    <div>
                        <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1">
                            Website URL
                        </label>
                        <div className="flex space-x-2">
                            <input
                                type="url"
                                id="url"
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                placeholder="https://example.com"
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                required
                            />
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 flex items-center"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="animate-spin mr-2 h-4 w-4" />
                                        Generating...
                                    </>
                                ) : (
                                    'Generate Sitemap'
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </div>

            {sitemap && (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                        <h2 className="text-lg font-medium text-gray-900">Generated Sitemap</h2>
                        <div className="flex space-x-2">
                            <button
                                onClick={copyToClipboard}
                                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                                title="Copy to clipboard"
                            >
                                <Copy className="h-5 w-5" />
                            </button>
                            <button
                                onClick={downloadSitemap}
                                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                                title="Download sitemap.xml"
                            >
                                <Download className="h-5 w-5" />
                            </button>
                            <a
                                href={`/api/sitemap?url=${encodeURIComponent(url)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                                title="Open in new tab"
                            >
                                <ExternalLink className="h-5 w-5" />
                            </a>
                        </div>
                    </div>
                    <div className="p-4 overflow-auto max-h-96">
                        <pre className="text-sm text-gray-800 bg-gray-50 p-4 rounded-lg overflow-x-auto">
                            <code>{sitemap}</code>
                        </pre>
                    </div>
                </div>
            )}

            <div className="mt-8 text-sm text-gray-500">
                <p className="mb-2">
                    <strong>Note:</strong> This tool generates an XML sitemap by crawling the provided website. For large websites, it may take some time to complete.
                </p>
                <p>
                    The sitemap follows the XML sitemap protocol and can be submitted to search engines like Google and Bing.
                </p>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={sitemapGeneratorContent.title}
                    description={sitemapGeneratorContent.description}
                    content={sitemapGeneratorContent.content}
                    faqs={sitemapGeneratorContent.faqs}
                    relatedTools={sitemapGeneratorContent.relatedTools}
                />
            </div>
        </div>
    );
}
